(() => {
  const managerKey = '__splitHeroSlider';
  if (window[managerKey]) {
    window[managerKey].initializeWithin(document);
    return;
  }

  const instances = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initialize(root) {
    if (!root || instances.has(root) || typeof window.Splide !== 'function') return;

    const carousel = root.querySelector('[data-split-hero-carousel]');
    const slides = [...root.querySelectorAll('.split-hero__slide')];
    if (!carousel || !slides.length) return;

    const card = root.querySelector('.split-hero__card');
    const controls = root.querySelector('[data-split-hero-controls]');
    const dots = [...root.querySelectorAll('[data-split-hero-dot]')];
    const status = root.querySelector('[data-split-hero-status]');
    const count = root.querySelector('[data-split-hero-count]');
    const labelText = root.querySelector('[data-split-hero-label]');
    const divider = root.querySelector('.split-hero__status-divider');
    const prev = root.querySelector('[data-split-hero-prev]');
    const next = root.querySelector('[data-split-hero-next]');
    const toggle = root.querySelector('[data-split-hero-toggle]');
    const autoplayConfigured = root.dataset.autoplay === 'true' && slides.length > 1;
    const listeners = [];
    let wantsAutoplay = autoplayConfigured;
    let hovered = false;
    let focused = false;
    let editorSelected = false;
    let announceNextMove = false;
    let sizeFrame = 0;
    let destroyed = false;
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(scheduleSize) : null;

    const listen = (target, name, handler) => {
      if (!target) return;
      target.addEventListener(name, handler);
      listeners.push(() => target.removeEventListener(name, handler));
    };

    const splide = new window.Splide(carousel, {
      type: root.dataset.transition === 'slide' ? 'slide' : 'fade',
      rewind: true,
      perPage: 1,
      perMove: 1,
      speed: 500,
      arrows: false,
      pagination: false,
      keyboard: false,
      drag: slides.length > 1,
      noDrag: '.split-hero__button, button, input, textarea, select',
      autoplay: autoplayConfigured ? 'pause' : false,
      interval: Number(root.dataset.interval) || 6000,
      pauseOnHover: false,
      pauseOnFocus: false,
      direction: document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr',
      live: false,
      reducedMotion: { speed: 0, rewindSpeed: 0, autoplay: 'pause' }
    });

    function syncPlayback() {
      if (!autoplayConfigured) return;
      if (toggle) {
        toggle.hidden = reducedMotion.matches;
        toggle.textContent = wantsAutoplay ? 'Ⅱ' : '▶';
        toggle.setAttribute('aria-label', wantsAutoplay ? 'Pause automatic slides' : 'Resume automatic slides');
      }
      const shouldPlay = wantsAutoplay && !hovered && !focused && !editorSelected && !document.hidden && !reducedMotion.matches;
      if (shouldPlay) splide.Components.Autoplay.play();
      else splide.Components.Autoplay.pause();
      scheduleSize();
    }

    function fitMobilePagination(mobile, inset) {
      const navigation = controls?.querySelector('.split-hero__navigation');
      if (!navigation) return;
      const properties = ['--sh-indicator-gap-mobile', '--sh-indicator-px-mobile', '--sh-navigation-inset-mobile', '--sh-navigation-gap-mobile', '--sh-playback-size-mobile', '--sh-arrow-size-mobile', '--sh-indicator-scale-mobile', '--sh-playback-font-mobile'];
      if (!mobile) {
        properties.forEach(name => root.style.removeProperty(name));
        return;
      }
      const style = getComputedStyle(navigation);
      const arrowCount = root.dataset.showArrowsMobile === 'false' ? 0 : 2;
      const hasPlayback = toggle && !toggle.hidden;
      const dotGaps = Math.max(0, dots.length - 1);
      const groupGaps = arrowCount + (hasPlayback ? 1 : 0);
      const border = parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
      let gap = parseFloat(style.getPropertyValue('--sh-indicator-gap')) || 0;
      let padding = parseFloat(style.getPropertyValue('--sh-indicator-px')) || 0;
      let groupGap = 4;
      let playbackSize = hasPlayback ? 40 : 0;
      let arrowSize = 44;
      const width = () => dots.length * 24 + arrowCount * arrowSize + playbackSize + border + dotGaps * gap + groupGaps * groupGap + 2 * padding;
      const excess = () => Math.max(0, width() - carousel.clientWidth + 2 * inset);
      // Spend decorative space first, then compact playback and arrows, retaining 24px targets.
      if (dotGaps) gap = Math.max(0, gap - excess() / dotGaps);
      padding = Math.max(0, padding - excess() / 2);
      if (groupGaps) groupGap = Math.max(0, groupGap - excess() / groupGaps);
      if (hasPlayback) playbackSize = Math.max(24, playbackSize - excess());
      if (arrowCount) arrowSize = Math.max(24, arrowSize - excess() / arrowCount);
      const horizontalInset = Math.max(0, Math.min(inset, (carousel.clientWidth - width()) / 2));
      const compact = (hasPlayback && playbackSize < 40) || (arrowCount && arrowSize < 44);
      const values = [gap, padding, horizontalInset, groupGap, hasPlayback ? playbackSize : 40, arrowSize, compact ? 0.8 : 1, compact ? 12 : 16];
      properties.forEach((name, index) => {
        const value = `${values[index]}${name === '--sh-indicator-scale-mobile' ? '' : 'px'}`;
        if (root.style.getPropertyValue(name) !== value) root.style.setProperty(name, value);
      });
    }

    function measureSize() {
      sizeFrame = 0;
      if (destroyed) return;
      const mobile = window.matchMedia('(max-width: 767px)').matches;
      const rootStyle = getComputedStyle(root);
      const ratio = rootStyle.getPropertyValue(mobile ? '--sh-ratio-mobile' : '--sh-ratio-desktop').split('/').map(Number);
      const aspect = ratio.length === 2 && ratio.every(n => n > 0) ? ratio[0] / ratio[1] : mobile ? 4 / 5 : 21 / 9;
      const inset = parseFloat(rootStyle.getPropertyValue('--sh-control-inset')) || 0;
      fitMobilePagination(mobile, inset);
      const bar = controls?.querySelector(root.classList.contains('split-hero--nav-bar') ? '.split-hero__control-bar' : mobile ? '.split-hero__navigation' : '.split-hero__pagination');
      const clearance = bar ? Math.ceil(bar.getBoundingClientRect().height + inset + 8) : 0;
      root.style.setProperty('--sh-controls-clearance', `${clearance}px`);
      const minimumHeight = mobile ? Math.max(0, parseFloat(rootStyle.getPropertyValue('--sh-min-height-mobile')) || 0) : 0;
      let frameHeight = Math.max(carousel.clientWidth / aspect, minimumHeight);
      let belowHeight = 0;
      slides.forEach(slide => {
        const content = slide.querySelector('.split-hero__content');
        const copy = slide.querySelector('.split-hero__copy');
        if (!copy) return;
        const style = getComputedStyle(content);
        const height = copy.getBoundingClientRect().height + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
        if (mobile && slide.classList.contains('split-hero__slide--mobile-below')) belowHeight = Math.max(belowHeight, height);
        else frameHeight = Math.max(frameHeight, height);
      });
      for (const [name, value] of [['--sh-frame-height', frameHeight], ['--sh-below-height', belowHeight]]) {
        const pixels = `${Math.ceil(value)}px`;
        if (root.style.getPropertyValue(name) !== pixels) root.style.setProperty(name, pixels);
      }
    }

    function scheduleSize() {
      if (destroyed || sizeFrame) return;
      sizeFrame = requestAnimationFrame(measureSize);
    }

    function update() {
      const index = splide.index;
      const label = slides[index].dataset.slideLabel;
      status?.setAttribute('aria-live', announceNextMove ? 'polite' : 'off');
      if (count) count.textContent = `Slide ${index + 1} of ${slides.length}`;
      if (labelText) labelText.textContent = label || '';
      if (divider) divider.hidden = !label;
      announceNextMove = false;
      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
        dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
      });
      scheduleSize();
    }

    function navigate(destination, pauseAutoplay = true) {
      announceNextMove = true;
      if (pauseAutoplay) wantsAutoplay = false;
      syncPlayback();
      splide.go(destination);
    }

    listen(prev, 'click', () => navigate('<'));
    listen(next, 'click', () => navigate('>'));
    dots.forEach((dot, index) => listen(dot, 'click', () => navigate(index)));
    listen(carousel, 'keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      if (event.target.closest('a, button, input, textarea, select, [contenteditable]')) return;
      event.preventDefault();
      if (event.key === 'Home') navigate(0);
      else if (event.key === 'End') navigate(slides.length - 1);
      else {
        const forward = event.key === (document.documentElement.dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight');
        navigate(forward ? '>' : '<');
      }
    });
    if (toggle) listen(toggle, 'click', () => {
      wantsAutoplay = !wantsAutoplay;
      syncPlayback();
    });
    listen(root, 'mouseenter', () => { hovered = true; syncPlayback(); });
    listen(root, 'mouseleave', () => { hovered = false; syncPlayback(); });
    listen(root, 'focusin', () => { focused = true; syncPlayback(); });
    listen(root, 'focusout', event => {
      if (root.contains(event.relatedTarget)) return;
      focused = false;
      syncPlayback();
    });
    listen(document, 'visibilitychange', syncPlayback);
    listen(reducedMotion, 'change', syncPlayback);
    listen(window, 'resize', scheduleSize);
    splide.on('drag', () => { announceNextMove = true; wantsAutoplay = false; syncPlayback(); });
    splide.on('move', scheduleSize);
    splide.on('mounted moved', update);
    splide.mount();
    card.classList.add('is-ready');
    resizeObserver?.observe(carousel);
    if (controls) {
      ['.split-hero__pagination', '.split-hero__navigation', '.split-hero__status'].forEach(selector => resizeObserver?.observe(controls.querySelector(selector)));
    }
    slides.forEach(slide => {
      const copy = slide.querySelector('.split-hero__copy');
      if (copy) resizeObserver?.observe(copy);
    });
    measureSize();
    document.fonts?.ready.then(scheduleSize);
    syncPlayback();

    instances.set(root, {
      navigate,
      selectSlide(index) { navigate(index, false); },
      setEditorSelected(selected) { editorSelected = selected; syncPlayback(); },
      destroy() {
        destroyed = true;
        if (sizeFrame) cancelAnimationFrame(sizeFrame);
        resizeObserver?.disconnect();
        listeners.forEach(remove => remove());
        splide.destroy();
        card.classList.remove('is-ready');
        ['--sh-frame-height', '--sh-below-height', '--sh-controls-clearance'].forEach(name => root.style.removeProperty(name));
        fitMobilePagination(false, 0);
      }
    });
  }

  function rootsWithin(scope) {
    const roots = [];
    if (scope.matches?.('[data-split-hero]')) roots.push(scope);
    scope.querySelectorAll?.('[data-split-hero]').forEach(root => roots.push(root));
    return roots;
  }

  function initializeWithin(scope) { rootsWithin(scope).forEach(initialize); }
  function findRoot(target) { return target.closest?.('[data-split-hero]') || target.querySelector?.('[data-split-hero]'); }

  window[managerKey] = { initializeWithin };
  if (typeof window.Splide === 'function') initializeWithin(document);
  if (document.readyState !== 'complete') {
    document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
    window.addEventListener('load', () => initializeWithin(document), { once: true });
  }

  document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    rootsWithin(event.target).forEach(root => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  });
  document.addEventListener('shopify:section:select', event => instances.get(findRoot(event.target))?.setEditorSelected(true));
  document.addEventListener('shopify:section:deselect', event => instances.get(findRoot(event.target))?.setEditorSelected(false));
  document.addEventListener('shopify:block:select', event => {
    const root = findRoot(event.target);
    const instance = instances.get(root);
    if (!instance) return;
    instance.setEditorSelected(true);
    const slide = event.target.closest?.('.split-hero__slide');
    if (slide) instance.selectSlide([...root.querySelectorAll('.split-hero__slide')].indexOf(slide));
  });
  document.addEventListener('shopify:block:deselect', event => instances.get(findRoot(event.target))?.setEditorSelected(false));
})();
